import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, CheckCircle2, Loader2, BellRing } from "lucide-react";

const SEVERITY_CONFIG: Record<string, { cls: string; dot: string }> = {
  critical: { cls: "bg-red-100 text-red-800",    dot: "bg-red-500" },
  high:     { cls: "bg-orange-100 text-orange-800", dot: "bg-orange-500" },
  medium:   { cls: "bg-amber-100 text-amber-700",  dot: "bg-amber-400" },
};

const TYPE_LABEL: Record<string, string> = {
  mention_spike:     "Mention Spike",
  sentiment_shift:   "Sentiment Shift",
  competitor_surge:  "Competitor Surge",
  keyword_alert:     "Keyword Alert",
};

export function CrisisCenter() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"open" | "resolved">("open");

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ["crisis-alerts", orgId, filter],
    enabled: !!orgId,
    queryFn: async () => {
      let q = supabase
        .from("crisis_alerts" as any)
        .select("*")
        .eq("org_id", orgId!)
        .order("triggered_at", { ascending: false });
      q = filter === "open" ? (q as any).is("resolved_at", null) : (q as any).not("resolved_at", "is", null);
      const { data, error } = await (q as any);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await (supabase
        .from("crisis_alerts" as any)
        .update({ resolved_at: new Date().toISOString() })
        .eq("id", alertId) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Alert resolved." });
      qc.invalidateQueries({ queryKey: ["crisis-alerts", orgId] });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openCount = alerts.filter((a: any) => !a.resolved_at).length;

  return (
    <div className="space-y-5">
      {/* Header stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={openCount > 0 ? "border-red-200 bg-red-50/30" : ""}>
          <CardContent className="p-4">
            <div className={`text-2xl font-bold ${openCount > 0 ? "text-red-700" : "text-[#0D2240]"}`}>{openCount}</div>
            <div className="text-xs text-muted-foreground">Open alerts</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{alerts.filter((a: any) => a.severity === "critical").length}</div>
            <div className="text-xs text-muted-foreground">Critical</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-600">{alerts.filter((a: any) => a.resolved_at).length}</div>
            <div className="text-xs text-muted-foreground">Resolved</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {(["open", "resolved"] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border transition-colors ${
              filter === f ? "bg-[#0D2240] text-white border-transparent" : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {f === "open" ? "Open" : "Resolved"}
          </button>
        ))}
      </div>

      {/* Alert list */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-16">
          <BellRing className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">
            {filter === "open" ? "No open alerts — you're all clear." : "No resolved alerts yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert: any) => {
            const sc = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.medium;
            return (
              <Card key={alert.id} className={alert.severity === "critical" ? "border-red-300" : ""}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sc.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm">{alert.title}</p>
                        <Badge className={`text-[10px] ${sc.cls}`}>{alert.severity}</Badge>
                        <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {TYPE_LABEL[alert.alert_type] ?? alert.alert_type}
                        </span>
                      </div>
                      <p className="text-justify text-xs text-muted-foreground mt-1">{alert.message}</p>
                      {alert.mention_count != null && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {alert.mention_count} mentions vs baseline {alert.baseline_count ?? "—"}
                          {alert.spike_ratio != null && ` (${(alert.spike_ratio * 100).toFixed(0)}% spike)`}
                          {alert.platform && ` · ${alert.platform}`}
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {alert.triggered_at ? new Date(alert.triggered_at).toLocaleString("en-GB") : ""}
                        {alert.resolved_at && ` · Resolved ${new Date(alert.resolved_at).toLocaleString("en-GB")}`}
                      </p>
                    </div>
                    {!alert.resolved_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1 shrink-0 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => resolveMutation.mutate(alert.id)}
                        disabled={resolveMutation.isPending}
                      >
                        <CheckCircle2 className="w-3 h-3" /> Resolve
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
